/* C16 · qt-gift.js — gift with purchase: the mechanic, not the action. Spec: 01-componenten.md C16.
   Add/remove (only when theme setting qt_gift_enabled is on): after every cart change, inside the
   gift window (checked here, at view time) and with the subtotal excluding the gift >= the threshold,
   add the gift's first available variant with line property _qt_gift = 1; otherwise remove it. Never
   more than one. The EUR 0 price comes from the automatic Buy X Get Y discount in Admin [P13].
   Pop-up: shown inside the window after scrolling past POPUP_REVEAL of the first viewport; closable.
   Invalid window dates disable the module with a console warning. */
import { onCartUpdate, onScroll, parseDate, subtotalWithoutGift, fetchCart } from '@qt/core';
import { CartLinesUpdateEvent } from '@shopify/events';

const POPUP_REVEAL = 0.8; // share of the first viewport scrolled before the pop-up appears (concept)
const MAX_TIMER = 2 ** 31 - 1; // setTimeout limit

function readConfig() {
  try {
    return JSON.parse(document.getElementById('qt-gift-config')?.textContent || 'null');
  } catch {
    return null;
  }
}

const config = readConfig();
const start = config && parseDate(config.start, 'qt_gift_start');
const end = config && parseDate(config.end, 'qt_gift_end');
// A sold-out gift is never added and never promised (pop-up/progress hide it too; Milo 24-09).
const valid = Boolean(config && config.available && start !== null && end !== null && config.threshold > 0);
const isLive = () => valid && Date.now() >= start && Date.now() <= end;

/* ---------- add / remove ------------------------------------------------------------------ */

let running = false;
let rerun = false;
let addFailed = false;

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);
}

/** Change the cart and tell Horizon (drawer, count bubble, progress bars) the way its own forms do. */
async function mutate(action, lines, request) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'standard-action', lines, promise: deferred.promise }));
  try {
    const sections = cartSectionIds();
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}${request.path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...request.body, sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const result = await response.json();
    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: result.sections, source: 'qt-gift', didError: !response.ok },
    });
    return response.ok;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

async function sync() {
  if (!valid || !config.enabled || !config.variantId) return;
  if (running) {
    rerun = true;
    return;
  }
  running = true;
  try {
    const cart = await fetchCart();
    const giftLines = cart.items.filter((item) => item.properties && item.properties._qt_gift);
    const qualifies = isLive() && subtotalWithoutGift(cart) >= config.threshold;

    if (qualifies && giftLines.length === 0 && !addFailed) {
      const ok = await mutate('add', [{ merchandiseId: String(config.variantId), quantity: 1 }], {
        path: 'cart/add.js',
        body: { items: [{ id: config.variantId, quantity: 1, properties: { _qt_gift: '1' } }] },
      });
      if (!ok) {
        addFailed = true; // e.g. sold out: do not retry on every cart change
        console.warn('[qt] gift could not be added to the cart');
      }
    } else if (!qualifies && giftLines.length > 0) {
      for (const line of giftLines) {
        await mutate('remove', [{ id: line.key, quantity: 0 }], { path: 'cart/change.js', body: { id: line.key, quantity: 0 } });
      }
    } else if (qualifies && (giftLines.length > 1 || giftLines[0]?.quantity > 1)) {
      const [keep, ...extra] = giftLines;
      if (keep.quantity > 1) {
        await mutate('update', [{ id: keep.key, quantity: 1 }], { path: 'cart/change.js', body: { id: keep.key, quantity: 1 } });
      }
      for (const line of extra) {
        await mutate('remove', [{ id: line.key, quantity: 0 }], { path: 'cart/change.js', body: { id: line.key, quantity: 0 } });
      }
    }
  } catch (error) {
    console.warn('[qt] gift sync failed', error);
  } finally {
    running = false;
    if (rerun) {
      rerun = false;
      sync();
    }
  }
}

/* ---------- pop-up ------------------------------------------------------------------------ */

/** The calendar dates as written in the settings (shop time), whatever the visitor's time zone. */
function formatPeriod() {
  const day = (iso) => {
    const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  };
  try {
    const locale = document.documentElement.lang || undefined;
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' }).formatRange(
      day(config.start),
      day(config.end)
    );
  } catch {
    return '';
  }
}

function setupPopup() {
  const popup = document.querySelector('[data-qt-gift-popup]');
  if (!popup) return;
  if (!isLive()) {
    popup.remove();
    return;
  }

  const stop = onScroll((top) => {
    if (top < window.innerHeight * POPUP_REVEAL) return;
    popup.hidden = false;
    stop();
  });

  popup.querySelector('[data-gift-close]')?.addEventListener('click', () => {
    popup.remove();
    stop();
  });
}

/* ---------- start ------------------------------------------------------------------------- */

/** Gift meta lines (pop-up C16, promo section on the early-access page): fill in the period. */
function fillMeta() {
  for (const meta of document.querySelectorAll('[data-gift-meta]')) {
    meta.textContent = meta.textContent.replace('[period]', formatPeriod());
    meta.hidden = false;
  }
}

if (valid) {
  fillMeta();
  setupPopup();
  if (config.enabled) {
    onCartUpdate(sync);
    sync();
    // A page left open across the window's start or end re-checks the cart at that moment.
    for (const moment of [start, end + 1000]) {
      const wait = moment - Date.now();
      if (wait > 0 && wait < MAX_TIMER) setTimeout(sync, wait);
    }
  }
}
